import api from '../api';

export interface Vulnerability {
  id: number;
  tenant_id: number;
  vulnerability_id: string;
  title: string;
  description: string | null;
  source: 'npm_audit' | 'dependabot' | 'semgrep' | 'snyk' | 'trivy' | 'manual';
  severity: 'zero_day' | 'critical' | 'high' | 'medium' | 'low' | 'info';
  cvss_score: number | null;
  cvss_vector: string | null;
  cve_id: string | null;
  cwe_id: string | null;
  package_name: string | null;
  package_version: string | null;
  fixed_version: string | null;
  file_path: string | null;
  line_number: number | null;
  affected_component: string | null;
  status: 'open' | 'in_progress' | 'resolved' | 'risk_accepted' | 'false_positive';
  priority: 'critical' | 'high' | 'medium' | 'low';
  sla_days: number;
  discovered_at: string;
  due_date: string;
  resolved_at: string | null;
  assigned_to: number | null;
  assigned_at: string | null;
  remediation_notes: string | null;
  remediation_steps: string[];
  references: { url: string; type: string }[];
  tags: string[];
  is_sla_breached: boolean;
  escalation_level: number;
  created_at: string;
  updated_at: string;
  assignee?: { id: number; name: string; email: string };
  slaStatus?: {
    status: 'on_track' | 'warning' | 'critical' | 'breached' | 'completed';
    daysRemaining: number;
    percentUsed: number;
  };
}

export interface VulnerabilityRiskAcceptance {
  id: number;
  vulnerability_id: number;
  status: 'pending' | 'approved' | 'rejected';
  justification: string;
  business_impact: string | null;
  compensating_controls: string | null;
  expiration_date: string | null;
  requested_by: number;
  requested_at: string;
  reviewed_by: number | null;
  reviewed_at: string | null;
  review_notes: string | null;
  requester?: { id: number; name: string; email: string };
  reviewer?: { id: number; name: string; email: string };
}

export interface VulnerabilityHistory {
  id: number;
  vulnerability_id: number;
  action: string;
  field_name: string | null;
  old_value: string | null;
  new_value: string | null;
  notes: string | null;
  performed_by: number | null;
  performed_at: string;
  performer?: { id: number; name: string };
}

export interface VulnerabilityNotification {
  id: number;
  vulnerability_id: number | null;
  notification_type: string;
  channel: string;
  subject: string | null;
  message: string;
  status: 'pending' | 'sent' | 'failed' | 'read';
  created_at: string;
  read_at: string | null;
  vulnerability?: { id: number; title: string; severity: string; status: string };
}

export interface DashboardMetrics {
  total: number;
  open: number;
  inProgress: number;
  resolved: number;
  riskAccepted: number;
  breached: number;
  dueSoon: number;
  recentResolved: number;
  bySeverity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  bySource: Record<string, number>;
  mttrDays: number;
  complianceScore: number;
  slaCompliance: number;
}

export interface QuarterlyReport {
  period: {
    year: number;
    quarter: number;
    startDate: string;
    endDate: string;
  };
  summary: {
    discovered: number;
    resolved: number;
    openAtEnd: number;
    slaBreaches: number;
    riskAccepted: number;
    mttrDays: number;
    resolutionRate: number;
    slaComplianceRate: number;
  };
  bySeverity: Record<string, number>;
  bySource: Record<string, number>;
}

export interface VulnerabilityFilters {
  status?: string | string[];
  severity?: string | string[];
  source?: string;
  assigned_to?: number;
  is_sla_breached?: boolean;
  search?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  limit?: number;
  offset?: number;
}

export interface VulnerabilitySettings {
  id?: number;
  tenant_id?: number;
  // SLA Configuration (days by severity)
  sla_zero_day: number;
  sla_critical: number;
  sla_high: number;
  sla_medium: number;
  sla_low: number;
  sla_info: number;
  // Notification Settings
  notify_new_vulnerability: boolean;
  notify_sla_warning: boolean;
  notify_sla_warning_threshold: number;
  notify_escalation: boolean;
  notify_escalation_threshold: number;
  notify_sla_breach: boolean;
  /** Roles that receive vulnerability email alerts. Default: Super Admins only. */
  notify_recipient_roles: Array<'SUPER_ADMIN' | 'ADMIN'>;
  // Auto-assignment settings
  auto_assign_enabled: boolean;
  default_assignee_id: number | null;
  updated_at?: string;
}

const vulnerabilityAPI = {
  async getDashboardMetrics(): Promise<DashboardMetrics> {
    const response = await api.get('/api/vulnerabilities/dashboard');
    return response.data;
  },

  async getVulnerabilities(filters?: VulnerabilityFilters): Promise<Vulnerability[]> {
    const response = await api.get('/api/vulnerabilities', { params: filters });
    return response.data;
  },

  async getVulnerabilityById(id: number): Promise<Vulnerability & { history: VulnerabilityHistory[]; riskAcceptances: VulnerabilityRiskAcceptance[] }> {
    const response = await api.get(`/api/vulnerabilities/${id}`);
    return response.data;
  },

  async updateVulnerability(id: number, updates: {
    status?: string;
    assigned_to?: number | null;
    priority?: string;
    remediation_notes?: string;
  }): Promise<Vulnerability> {
    const response = await api.put(`/api/vulnerabilities/${id}`, updates);
    return response.data;
  },

  async requestRiskAcceptance(vulnerabilityId: number, data: {
    justification: string;
    business_impact?: string;
    compensating_controls?: string;
    expiration_date?: string;
  }): Promise<VulnerabilityRiskAcceptance> {
    const response = await api.post(`/api/vulnerabilities/${vulnerabilityId}/risk-acceptance`, data);
    return response.data;
  },

  async reviewRiskAcceptance(acceptanceId: number, decision: 'approved' | 'rejected', reviewNotes?: string): Promise<VulnerabilityRiskAcceptance> {
    const response = await api.put(`/api/vulnerabilities/risk-acceptance/${acceptanceId}/review`, {
      decision,
      review_notes: reviewNotes
    });
    return response.data;
  },

  async getPendingApprovals(): Promise<VulnerabilityRiskAcceptance[]> {
    const response = await api.get('/api/vulnerabilities/pending-approvals');
    return response.data;
  },

  async getVulnerabilityHistory(id: number, limit?: number): Promise<VulnerabilityHistory[]> {
    const response = await api.get(`/api/vulnerabilities/${id}/history`, { params: { limit } });
    return response.data;
  },

  async scanNpmAudit(): Promise<{ message: string; found: number; imported: number; skipped: number }> {
    const response = await api.post('/api/vulnerabilities/scan/npm-audit');
    return response.data;
  },

  async importDependabot(alerts: unknown[]): Promise<{ message: string; found: number; imported: number; skipped: number }> {
    const response = await api.post('/api/vulnerabilities/import/dependabot', { alerts });
    return response.data;
  },

  async importSemgrep(results: unknown[]): Promise<{ message: string; found: number; imported: number; skipped: number }> {
    const response = await api.post('/api/vulnerabilities/import/semgrep', { results });
    return response.data;
  },

  async syncSecurityScans(): Promise<{ message: string; imported: number; skipped: number }> {
    const response = await api.post('/api/vulnerabilities/sync/security-scans');
    return response.data;
  },

  async checkSLABreaches(): Promise<{ message: string; breached: number; escalated: number }> {
    const response = await api.post('/api/vulnerabilities/check-sla');
    return response.data;
  },

  async getQuarterlyReport(year?: number, quarter?: number): Promise<QuarterlyReport> {
    const response = await api.get('/api/vulnerabilities/report/quarterly', { params: { year, quarter } });
    return response.data;
  },

  async downloadQuarterlyReportPDF(year?: number, quarter?: number): Promise<Blob> {
    const response = await api.get('/api/vulnerabilities/report/quarterly/pdf', {
      params: { year, quarter },
      responseType: 'blob'
    });
    return response.data;
  },

  async getNotifications(unreadOnly?: boolean, limit?: number): Promise<VulnerabilityNotification[]> {
    const response = await api.get('/api/vulnerabilities/notifications', {
      params: { unreadOnly, limit }
    });
    return response.data;
  },

  async markNotificationRead(notificationId: number): Promise<void> {
    await api.put(`/api/vulnerabilities/notifications/${notificationId}/read`);
  },

  // Settings
  async getSettings(): Promise<VulnerabilitySettings> {
    const response = await api.get('/api/vulnerabilities/settings');
    return response.data;
  },

  async updateSettings(settings: Partial<VulnerabilitySettings>): Promise<VulnerabilitySettings> {
    const response = await api.put('/api/vulnerabilities/settings', settings);
    return response.data;
  }
};

export default vulnerabilityAPI;
